import React from 'react';
import Box from '@mui/material/Box';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import {BadgeProps, ListItem, ListItemButton} from '@mui/material';
import {Fonts} from '../../../shared/constants/AppEnums';
import clsx from 'clsx';
import {NotificationData} from '../../services/db/notifications/notification';
import makeStyles from '@mui/styles/makeStyles';
import {ThreadDetailModel} from '../../../types/models/ThreadDetailModel';
import {ActivityGroupModel} from '../../../types/models/ActivityGroupModel';
import {hasItems} from '../../helpers/arrays';
import PersonAvatar from '../../../modules/people/PersonAvatar';
import CompanyAvatar from '../../../modules/companies/CompanyAvatar';
import {CurrentUserModel} from '../../../types/views/CurrentUserModel';
import {styled, Theme} from '@mui/material/styles';
import Badge from '@mui/material/Badge';
import PersonLink from '../../../modules/people/PersonLink';
import CompanyLink from '../../../modules/companies/CompanyLink';
import {AppNavLink} from '../../index';
import {generatePath} from 'react-router';
import {inboxPaths} from '../../../modules/messaging/paths';

const useStyles = makeStyles(() => ({
  textBase: {
    fontSize: 14,
  },
  avatar: {
    width: 48,
    height: 48,
  },
  minWidth0: {
    minWidth: 0,
  },
  listItemRoot: {
    padding: '8px 10px',
  },
}));

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: 2,
    top: 10,
    border: `1px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
  },
}));

const useThreadItemStyles = makeStyles((theme: Theme) => ({
  groupItem: {
    display: 'flex',
    flexDirection: 'row',
    color: theme.palette.text.primary,
    '&.MuiListItem-root': {
      paddingTop: '2px',
      paddingBottom: '5px',
      paddingLeft: '18px',
      paddingRight: '12px',
    },
    '& .MuiListItemText-root': {
      flex: 1,
      display: 'block'
    },
  },
  textTruncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface IThreadMetadata {
  recipientIds: string[] | undefined;
  internalIds: string[] | undefined;
  recipientCompanyId: string | undefined;
  companyThread: boolean;
}

interface NotificationItemProps {
  threadItem: ThreadDetailModel;
  currentUser: CurrentUserModel | undefined;
}

const NotificationItem: React.FC<NotificationItemProps> = ({threadItem, currentUser}) => {
  const classes = useStyles();
  const threadItemClasses = useThreadItemStyles();

  const metadataFromThread = (): IThreadMetadata => {
    let recipientIds: string[] = [];
    let internalIds: string[] = [];
    let recipientCompanyId: string | undefined = undefined;
    let companyThread = false;
    // If this is a conversation with a student or contact
    if (hasItems(threadItem.recipientPeopleMetadata)) {
      recipientIds = threadItem.recipientPeopleMetadata?.filter(x => !x.isInternal).map(x => x.personId) ?? [];
      internalIds = threadItem.recipientPeopleMetadata?.filter(x => x.isInternal).map(x => x.personId) ?? [];
    }
    if (threadItem.recipientCompaniesMetadata && hasItems(threadItem.recipientCompaniesMetadata)){
      companyThread = true;
      recipientCompanyId = threadItem.recipientCompaniesMetadata[0].companyId;
    }
    if (!recipientIds || recipientIds.length === 0) {
      // We have no external recipients, just show the other advisers.
      if (threadItem.recipientPeopleMetadata && hasItems(threadItem.recipientPeopleMetadata)) {
        recipientIds = threadItem.recipientPeopleMetadata.filter(x => x.personId !== currentUser?.personId).map(x => x.personId);
      }
    }
    return {
      recipientIds, recipientCompanyId, internalIds, companyThread
    };
  }

  const avatarForRecipientList = () => {
    const threadMetadata = metadataFromThread();
    if (!threadMetadata.recipientIds && !threadMetadata.recipientCompanyId) {
      return <StyledBadge badgeContent={threadItem.unreadCount} color="secondary"><Avatar sx={{ fontSize: 36 }} className={classes.avatar} /></StyledBadge>;
    }
    if (threadMetadata.recipientIds && hasItems(threadMetadata.recipientIds)) return <StyledBadge badgeContent={threadItem.unreadCount} color="secondary"><PersonAvatar size={36} personId={threadMetadata.recipientIds[0]} /></StyledBadge>;
    return <StyledBadge badgeContent={threadItem.unreadCount} color="secondary"><CompanyAvatar size={36} companyId={threadMetadata.recipientCompanyId??''} /></StyledBadge>;
  }

  const threadTitle = () => {
    const threadMetadata = metadataFromThread();
    if (threadMetadata.recipientIds && hasItems(threadMetadata.recipientIds)) {
      return <Box component="h5" className={threadItemClasses.textTruncate}>
        {threadMetadata.recipientIds?.map((personId: string, idx: number) => <React.Fragment key={`${personId}-${idx}-rec-ids`}>{idx > 0 ? ', ' : ''}<PersonLink
          personId={personId} /></React.Fragment>)}
      </Box>;
    }
    if (threadMetadata.recipientCompanyId) {
      return <Box component="h5" className={threadItemClasses.textTruncate}>
        <CompanyLink companyId={threadMetadata.recipientCompanyId} />
      </Box>;
    }
    return <Box component="h5" className={threadItemClasses.textTruncate}>Context not found</Box>;
  }

  return (
    <ListItemButton
      key={`hdr-not-${threadItem.id}`}
      component={AppNavLink}
      to={generatePath(inboxPaths.InboxThreadView, { id: threadItem.id })}
      className={clsx(classes.listItemRoot, 'item-hover')}>
      <Box mr={4}>
        <ListItemAvatar className={classes.minWidth0}>
          {avatarForRecipientList()}
        </ListItemAvatar>
      </Box>
      <Box className={classes.textBase} color='text.secondary'>
        <Box
          mr={2}
          component='span'
          display='inline-block'
          color='text.primary'
          fontWeight={Fonts.MEDIUM}>
          {threadTitle()}
        </Box>
        <Box className={threadItemClasses.textTruncate}>{threadItem.lastMessage??'Other activity'}</Box>
      </Box>
    </ListItemButton>
  );
};

export default NotificationItem;
